import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Lenis from 'lenis';
import {
  ArrowRight,
  BadgeCheck,
  CalendarDays,
  Car,
  ChefHat,
  ChevronRight,
  Clock3,
  Crown,
  Gem,
  Mail,
  MapPin,
  Music2,
  Phone,
  ShieldCheck,
  Sparkles,
  Users,
  Volume2,
  VolumeX,
  Waves,
  Wine,
} from 'lucide-react';
import CountdownTimer from '../components/CountdownTimer';
import PaymentModal from '../components/PaymentModal';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 48 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

const EventMedia = ({ event }) => {
  const images = event.images || [event.image];
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    if (images.length < 2) return undefined;
    const interval = window.setInterval(() => {
      setImageIndex((current) => (current + 1) % images.length);
    }, 4500);
    return () => window.clearInterval(interval);
  }, [images.length]);

  const showImage = (nextIndex) => {
    setImageIndex((nextIndex + images.length) % images.length);
  };

  const handleDragEnd = (_event, info) => {
    if (images.length < 2) return;
    if (info.offset.x < -50) showImage(imageIndex + 1);
    if (info.offset.x > 50) showImage(imageIndex - 1);
  };

  return (
    <>
      <div className="event-media" data-testid={`${event.testId}-media`}>
        <motion.div
          className="event-media-track"
          animate={{ x: `-${imageIndex * 100}%` }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          drag={images.length > 1 ? 'x' : false}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.18}
          onDragEnd={handleDragEnd}
        >
          {images.map((image, index) => (
            <img
              key={image}
              src={image}
              alt={`${event.title}, photo ${index + 1}`}
              draggable="false"
            style={{ objectPosition: event.imagePosition || 'center' }}
            />
          ))}
        </motion.div>
      </div>
      {images.length > 1 && (
        <div className="event-media-dots" aria-label={`${event.title} photo selector`}>
          {images.map((image, index) => (
            <button
              type="button"
              key={image}
              aria-label={`Show ${event.title} photo ${index + 1}`}
              aria-pressed={imageIndex === index}
              className={imageIndex === index ? 'active' : ''}
              data-testid={`${event.testId}-photo-${index + 1}`}
              onClick={() => showImage(index)}
            >
              <span />
            </button>
          ))}
        </div>
      )}
    </>
  );
};

const IntroVideo = ({ onFinish }) => {
  const videoRef = useRef(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [videoSrc] = useState(() => {
    const probe = document.createElement('video');
    return probe.canPlayType('video/mp4; codecs="avc1.42E01E"')
      ? '/assets/promo-intro.mp4'
      : '/assets/promo-intro.webm';
  });

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const closeIntro = () => {
    if (isClosing) return;
    setIsClosing(true);
    window.setTimeout(onFinish, 650);
  };

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  return (
    <div className={`intro-video-overlay${isClosing ? ' closing' : ''}`} data-testid="intro-video-overlay">
      <video
        className="intro-video intro-video-backdrop"
        src={videoSrc}
        autoPlay
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
        tabIndex={-1}
      />
      <video
        ref={videoRef}
        className="intro-video intro-video-main"
        src={videoSrc}
        poster="/assets/promo-poster.webp"
        autoPlay
        muted
        playsInline
        preload="auto"
        onEnded={closeIntro}
        onError={onFinish}
        data-testid="intro-video"
      />
      <div className="intro-video-actions">
        <button type="button" className="intro-sound-button" onClick={toggleSound} data-testid="intro-sound-toggle-button">
          {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          <span>{isMuted ? 'Sound off' : 'Sound on'}</span>
        </button>
        <button type="button" className="intro-skip-button" onClick={closeIntro} data-testid="intro-skip-button">
          Skip intro
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedAccommodation, setSelectedAccommodation] = useState('condo');
  const [showIntro, setShowIntro] = useState(true);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const cardRotate = useTransform(scrollYProgress, [0, 1], [0, -5]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.25, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    const referralCode = new URLSearchParams(window.location.search).get('ref');
    if (!referralCode) return;

    localStorage.setItem('playcation_referral_code', referralCode);
    fetch(`${API}/referrals/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: referralCode }),
    }).catch((error) => console.error('Unable to track referral visit:', error));
  }, []);

  const openBooking = (type) => {
    setSelectedAccommodation(type);
    setShowPaymentModal(true);
  };

  const inclusions = [
    { icon: ChefHat, title: 'Private Chef', description: 'Breakfast, lunch, and dinner prepared daily.' },
    { icon: Wine, title: 'Premium Liquor', description: 'Top-shelf spirits and signature cocktails.' },
    { icon: Crown, title: 'Private Estates', description: 'Condos and villa rooms reserved for our guests.' },
    { icon: Music2, title: 'Curated Entertainment', description: 'Professional hosts, DJs, and performers.' },
    { icon: Car, title: 'Ground Transport', description: 'Scheduled movement between curated experiences.' },
    { icon: Sparkles, title: 'VIP Concierge', description: 'Priority assistance throughout the weekend.' },
    { icon: Waves, title: 'Poolside Access', description: 'Day parties and luxury lounge moments.' },
    { icon: ShieldCheck, title: 'Secure Booking', description: 'Stripe-powered checkout and confirmation.' },
  ];

  const accommodations = [
    {
      type: 'condo',
      title: 'Luxury Oceanfront Condo',
      eyebrow: 'Group Comfort',
      price: 2500,
      capacity: '1-6 guests',
      deposit: '$500 per person',
      image: '/assets/pineapple-vista-entrance.webp',
      features: ['Private oceanview balcony', 'Designer king suites', 'Full kitchen and dining area', 'VIP welcome package'],
      testId: 'accommodation-card-condo',
      buttonTestId: 'book-condo-button',
    },
    {
      type: 'villa',
      title: 'Royal VIP Villa',
      eyebrow: 'Main Party Residence',
      price: 3000,
      capacity: '1-3 guests',
      deposit: '$600 per person',
      image: '/assets/pineapple-vista-main-house-pool.webp',
      features: ['Private infinity pool', 'Premium stocked bar', 'Front-row event access', 'Ultra VIP concierge'],
      testId: 'accommodation-card-villa',
      buttonTestId: 'book-villa-button',
    },
  ];

  const exclusiveEvents = [
    {
      order: '01',
      title: 'All White Party',
      tagline: 'Opulent elegance under the Montego Bay stars',
      description: 'A crisp white kickoff with champagne, private resort energy, and golden-hour arrivals.',
      image: '/assets/all-white-party-1.webp',
      images: [
        '/assets/all-white-party-1.webp',
        '/assets/all-white-party-2.webp',
        '/assets/all-white-party-3.webp',
      ],
      testId: 'event-all-white-party',
    },
    {
      order: '02',
      title: 'Sun Buns & Water Guns',
      tagline: 'Daytime poolside kinetic energy',
      description: 'A high-octane Jamaican color-themed pool party with cocktails, water battles, and swimwear style.',
      image: '/assets/sun-buns-patio-pool.webp',
      imagePosition: 'center 48%',
      testId: 'event-sun-buns-water-guns',
    },
    {
      order: '03',
      title: 'Night of Forbidden Fruit',
      tagline: 'A crimson-lit midnight fantasy',
      description: 'The signature after-dark affair with sensual decor, crimson lighting, and elevated adult entertainment.',
      image: '/assets/forbidden-fruit-night-pool.webp',
      testId: 'event-night-forbidden-fruit',
    },
    {
      order: '04',
      title: 'Hedo Day Crash',
      tagline: 'Exclusive resort takeover energy',
      description: 'An optional day-pass excursion featuring VIP cabanas, beach-club energy, and pulse-pounding DJ sets.',
      image: '/assets/hedo-day-crash-1.webp',
      images: [
        '/assets/hedo-day-crash-1.webp',
        '/assets/hedo-day-crash-2.webp',
        '/assets/hedo-day-crash-3.webp',
      ],
      testId: 'event-hedo-day-crash',
    },
    {
      order: '05',
      title: 'Sunset Cruise',
      tagline: 'A golden-hour charter finale',
      description: 'A private farewell cruise across Caribbean water with an open premium bar and sunset views.',
      image: '/assets/sunset-cruise-catamaran.webp',
      imagePosition: 'center 18%',
      testId: 'event-sunset-cruise',
    },
  ];

  const itineraryDays = [
    {
      date: 'Friday, July 16',
      title: 'Arrival & White Opening',
      moments: ['Private arrival window and resort check-in', 'Welcome cocktails and chef-led dinner', 'All White Party under the Montego Bay stars'],
      testId: 'itinerary-day-july-16',
    },
    {
      date: 'Saturday, July 17',
      title: 'Pool Heat & Midnight Fantasy',
      moments: ['Sun Buns & Water Guns daytime pool party', 'Golden-hour lounge and premium bar reset', 'Night of Forbidden Fruit after-dark signature event'],
      testId: 'itinerary-day-july-17',
    },
    {
      date: 'Sunday, July 18',
      title: 'Resort Crash & Sunset Finale',
      moments: ['Hedo Day Crash optional resort excursion', 'Farewell toast and private boarding', 'Sunset Cruise across the Caribbean water'],
      testId: 'itinerary-day-july-18',
    },
  ];

  const marqueeItems = [
    'Royal Purple Luxury',
    'All White Party',
    'Night of Forbidden Fruit',
    'Montego Bay 2027',
    'Exclusive VIP Access',
    'Sunset Cruise Finale',
  ];

  const heroLines = ["Siege's", 'Playcation', 'Montego Bay'];

  return (
    <div className="landing-page" data-testid="landing-page">
      {showIntro && <IntroVideo onFinish={() => setShowIntro(false)} />}
      <header className="site-header" data-testid="site-header">
        <a href="#top" className="header-brand" data-testid="header-logo">
          <span className="header-logo-frame">
            <img src="/king-siege-logo-new.jpeg" alt="King Siege LLC" />
          </span>
          <span>
            <span className="header-brand-name">King Siege LLC</span>
            <span className="header-brand-subtitle">The Royal Playcation</span>
          </span>
        </a>
        <nav className="header-nav" aria-label="Primary navigation">
          <a href="#villas">Stays</a>
          <a href="#events">Events</a>
          <a href="#itinerary">Itinerary</a>
          <a href="#investment">Investment</a>
        </nav>
        <Button className="header-cta" data-testid="header-deposit-button" onClick={() => openBooking('condo')}>
          Reserve
          <ArrowRight className="h-4 w-4" />
        </Button>
      </header>

      <section id="top" ref={heroRef} className="hero-stage" data-testid="hero-section">
        <div className="hero-aurora hero-aurora-one" />
        <div className="hero-aurora hero-aurora-two" />
        <div className="hero-grain" />

        <div className="hero-grid">
          <motion.div className="hero-copy" variants={stagger} initial="hidden" animate="show">
            <motion.p className="eyebrow" variants={fadeUp} data-testid="hero-eyebrow">
              King Siege LLC presents
            </motion.p>
            <h1 className="hero-title" aria-label="Siege's Playcation Montego Bay">
              {heroLines.map((line, index) => (
                <span className="masked-line" key={line}>
                  <motion.span
                    custom={index}
                    initial={{ y: '115%', rotate: 2 }}
                    animate={{ y: '0%', rotate: 0 }}
                    transition={{ delay: 0.2 + index * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p className="hero-lede" variants={fadeUp}>
              A private three-day Caribbean escape where royal treatment, curated nightlife, and unapologetic luxury meet.
            </motion.p>
            <motion.div className="hero-meta" variants={fadeUp}>
              <span><CalendarDays className="h-4 w-4" /> July 16-18, 2027</span>
              <span><MapPin className="h-4 w-4" /> Montego Bay, Jamaica</span>
              <span><Users className="h-4 w-4" /> Limited adult guest list</span>
            </motion.div>
            <motion.div className="hero-actions" variants={fadeUp}>
              <Button className="gold-cta" data-testid="hero-deposit-cta-button" onClick={() => openBooking('condo')}>
                Lock in your deposit
                <ArrowRight className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                className="ghost-cta"
                data-testid="hero-text-king-button"
                onClick={() => window.location.href = `sms:8325965177?body=I'm ready for the Siege's Playcation!`}
              >
                <Phone className="h-5 w-5" />
                Text King
              </Button>
            </motion.div>
          </motion.div>

          <motion.div className="hero-visual" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.55, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}>
            <motion.div className="hero-photo-frame" style={{ y: imageY, scale: imageScale }}>
              <img
                src="/assets/pineapple-vista-pool.webp"
                alt="Pineapple Vista infinity pool with white loungers, umbrellas, and palm trees"
              />
            </motion.div>
            <motion.div className="hero-logo-card" style={{ y: cardY, rotate: cardRotate }}>
              <img src="/king-siege-logo-new.jpeg" alt="King Siege crown logo" />
            </motion.div>
            <motion.div className="hero-spec-card" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.05, duration: 0.8 }}>
              <span>Deposits from</span>
              <strong>$500</strong>
              <small>20% secures your place</small>
            </motion.div>
          </motion.div>
        </div>

        <motion.div className="hero-countdown" initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8 }}>
          <CountdownTimer targetDate="2027-07-16T00:00:00-05:00" />
        </motion.div>
      </section>

      <div className="royal-marquee" aria-hidden="true">
        <motion.div className="marquee-track" animate={{ x: ['0%', '-50%'] }} transition={{ duration: 42, ease: 'linear', repeat: Infinity }}>
          {[...marqueeItems, ...marqueeItems].map((item, index) => (
            <span key={`${item}-${index}`}><Gem className="h-4 w-4" /> {item}</span>
          ))}
        </motion.div>
      </div>

      <motion.section className="intro-section section-shell" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-120px' }}>
        <motion.div className="section-heading left-align" variants={fadeUp}>
          <p className="eyebrow">Not another resort weekend</p>
          <h2>A private takeover built for indulgence, connection, and cinematic memories.</h2>
        </motion.div>
        <motion.p className="intro-copy" variants={fadeUp}>
          Skip the crowded wristbands and buffet lines. Siege's Playcation is an intimate, high-touch retreat with chef-led dining, premium pours, signature parties, and a guest list curated for grown energy.
        </motion.p>
        <motion.div className="inclusion-bento" variants={stagger}>
          {inclusions.map((item) => (
            <motion.div className="inclusion-tile" key={item.title} variants={fadeUp} whileHover={{ y: -8, borderColor: 'rgba(212,175,55,.75)' }}>
              <item.icon className="h-6 w-6" />
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      <motion.section id="villas" className="accommodations-section section-shell" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-120px' }}>
        <motion.div className="section-heading" variants={fadeUp}>
          <p className="eyebrow">Choose your residence</p>
          <h2>Two ways to enter the royal weekender.</h2>
          <p>Guest count updates the total automatically at checkout, whether you book a condo for one to six guests or a villa room for one to three.</p>
        </motion.div>
        <div className="accommodation-grid">
          {accommodations.map((stay) => (
            <motion.article className="accommodation-card" key={stay.type} variants={fadeUp} whileHover={{ y: -10 }} data-testid={stay.testId}>
              <div className="accommodation-image-wrap">
                <img src={stay.image} alt={stay.title} />
                <span>{stay.eyebrow}</span>
              </div>
              <div className="accommodation-content">
                <div>
                  <h3>{stay.title}</h3>
                  <p className="accommodation-capacity">{stay.capacity}</p>
                </div>
                <div className="accommodation-price">
                  <span>${stay.price.toLocaleString()}</span>
                  <small>per person</small>
                </div>
                <ul>
                  {stay.features.map((feature) => (
                    <li key={feature}><BadgeCheck className="h-4 w-4" /> {feature}</li>
                  ))}
                </ul>
                <div className="accommodation-footer">
                  <span>{stay.deposit} deposit</span>
                  <Button className="outline-gold" data-testid={stay.buttonTestId} onClick={() => openBooking(stay.type)}>
                    Book this stay
                  </Button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </motion.section>

      <motion.section id="events" className="events-showcase section-shell" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-120px' }}>
        <motion.div className="section-heading" variants={fadeUp}>
          <p className="eyebrow">Five signature moments</p>
          <h2>A weekend sequenced like a royal fever dream.</h2>
        </motion.div>
        <div className="events-grid">
          {exclusiveEvents.map((event) => (
            <motion.article className="event-tile" key={event.title} variants={fadeUp} whileHover={{ y: -8 }} data-testid={event.testId}>
              <EventMedia event={event} />
              <div className="event-scrim" />
              <div className="event-copy">
                <span>{event.order}</span>
                <h3>{event.title}</h3>
                <strong>{event.tagline}</strong>
                <p>{event.description}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </motion.section>

      <motion.section id="itinerary" className="itinerary-section section-shell" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-120px' }} data-testid="itinerary-section">
        <motion.div className="section-heading" variants={fadeUp}>
          <p className="eyebrow">Three-day flow</p>
          <h2>Every day has a signature moment.</h2>
          <p>Arrival details and exact timing are shared with confirmed guests before departure.</p>
        </motion.div>
        <div className="itinerary-grid">
          {itineraryDays.map((day) => (
            <motion.article className="itinerary-card" key={day.date} variants={fadeUp} whileHover={{ y: -8 }} data-testid={day.testId}>
              <span>{day.date}</span>
              <h3>{day.title}</h3>
              <ul>
                {day.moments.map((moment) => (
                  <li key={moment}><Clock3 className="h-4 w-4" /> {moment}</li>
                ))}
              </ul>
            </motion.article>
          ))}
        </div>
      </motion.section>

      <motion.section id="investment" className="investment-section section-shell" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-120px' }}>
        <motion.div className="investment-panel" variants={fadeUp}>
          <div className="investment-copy">
            <p className="eyebrow">The investment</p>
            <h2>Secure the weekend with a 20% deposit.</h2>
            <p>
              Condo packages begin at $2,500 per person and Royal Villa packages begin at $3,000 per person. Flights are not included. Final balances are due 30 days before departure.
            </p>
            <div className="investment-actions">
              <Button className="gold-cta" data-testid="investment-book-button" onClick={() => openBooking('condo')}>
                Start secure checkout
                <ArrowRight className="h-5 w-5" />
              </Button>
              <a
                className="property-link"
                data-testid="balance-checkout-link"
                href="/balance"
              >
                Already booked? Pay your balance
              </a>
              <a
                className="property-link"
                data-testid="property-video-link"
                href="https://youtu.be/lgOG5h9LfTM?is=kPBECacpbduQ_-wN"
                target="_blank"
                rel="noopener noreferrer"
              >
                View the property
              </a>
            </div>
          </div>
        </motion.div>
      </motion.section>

      <motion.section className="final-cta-section" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-100px' }}>
        <motion.div className="final-cta-card" variants={fadeUp}>
          <Crown className="final-crown" />
          <p className="eyebrow">Your invitation is waiting</p>
          <h2>Claim your place in Montego Bay before the guest list closes.</h2>
          <p>Reserve online now, or contact King directly for questions and custom arrangements.</p>
          <div className="final-actions">
            <Button className="gold-cta" data-testid="final-deposit-button" onClick={() => openBooking('villa')}>
              Book the royal villa
              <ArrowRight className="h-5 w-5" />
            </Button>
            <Button className="ghost-cta" data-testid="final-email-button" onClick={() => window.location.href = 'mailto:siegeplaycations@gmail.com?subject=Inquiry about Siege\'s Playcation'}>
              <Mail className="h-5 w-5" />
              Email us
            </Button>
          </div>
        </motion.div>
      </motion.section>

      <footer className="site-footer">
        <div className="footer-brand">
          <img src="/king-siege-logo-new.jpeg" alt="King Siege LLC logo" />
          <div>
            <strong>Siege's Playcation</strong>
            <span>July 16-18, 2027 · Montego Bay, Jamaica</span>
          </div>
        </div>
        <div className="footer-links">
          <a href="sms:8325965177?body=I'm ready for the Siege's Playcation!" data-testid="footer-phone-link">Text King: 832-596-5177</a>
          <a href="mailto:siegeplaycations@gmail.com?subject=Inquiry about Siege's Playcation - Montego Bay" data-testid="footer-email-link">siegeplaycations@gmail.com</a>
        </div>
      </footer>

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={() => setShowPaymentModal(false)}
        initialAccommodation={selectedAccommodation}
      />
    </div>
  );
};

export default LandingPage;
